import { type Dispatch, type SetStateAction, useState } from "react";

import SectionCard from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { FieldMultiSort } from "@/appComponents/Blocks/Composites/FieldMultiSort/FieldMultiSort";
import { Flex, Search, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList, kPositionCountFields, kEmployeeCountFields } from "@coreapp/constants";

type ChartConfigPartial = {
  excel?: {
    fields: string[];
  };
  headers: string[];
};

type Props = {
  chartConfig?: ChartConfigPartial;
  handleUpdate: (chartConfig: Partial<ChartConfigPartial>, key: string) => void;
  dataTestIdPrefix: string;
};

export const ExcelExportFields = (props: Props) => {
  const [queryString, setQueryString] = useState("");
  const [fieldNames, setFieldNames] = useState(() => {
    const allFieldNames = getAllFieldNames(props);
    const includedFields = props.chartConfig?.excel?.fields ?? allFieldNames;

    const excludedFields = allFieldNames.filter(fieldName => !includedFields.includes(fieldName));

    return {
      Excluded: excludedFields,
      Included: includedFields
    };
  });

  const handleFieldsChange: Dispatch<SetStateAction<typeof fieldNames>> = dispatch => {
    const nextState = typeof dispatch === "function" ? dispatch(fieldNames) : dispatch;
    props.handleUpdate({ excel: { fields: nextState.Included } }, "excel");
    setFieldNames(nextState);
  };

  const handleToggleAll = (target: "Included" | "Excluded") => () => {
    const isIncludeAll = target === "Included";
    const allFields = getAllFieldNames(props);
    const nextState = {
      Excluded: isIncludeAll ? [] : allFields,
      Included: isIncludeAll ? allFields : []
    };
    props.handleUpdate({ excel: { fields: nextState.Included } }, "excel");
    setFieldNames(nextState);
  };

  const handleInput = (query: string) => {
    setQueryString(query);
  };

  const getFilteredFieldNames = () => {
    if (!queryString) {
      return fieldNames;
    }

    const lowerCaseQuery = queryString.toLowerCase();
    const filterFields = (fieldNames: string[]) => {
      return fieldNames.filter(fieldName => fieldName.toLowerCase().includes(lowerCaseQuery));
    };

    return {
      Excluded: filterFields(fieldNames.Excluded),
      Included: filterFields(fieldNames.Included)
    };
  };

  const showExcelExportFields = getFeatureAccess(enumFeaturesList.adminExcelFields);

  return (
    <SectionCard
      title={appstr("Fields allowed for Excel export")}
      titleContent={
        <Flex width={"300px"}>
          <Search
            dataTestId={`${props.dataTestIdPrefix}-excel-export-search`}
            size="medium"
            placeholder={appstr("findfield")}
            onInput={handleInput}
          />
        </Flex>
      }
    >
      <Flex height={"100%"} direction="column" gap="8px">
        <Flex padding={"6px"}>
          <Text color="grey" size="small">
            {appstr(
              "By default, users can choose any fields permitted by their security overlay to export to excel. This feature allows you to optionally customize the list of fields your users will have access to downloading in their excel file"
            )}
          </Text>
        </Flex>
        <Flex flex={1} width="100%" overflow="hidden">
          {showExcelExportFields && (
            <FieldMultiSort
              dataTestIdPrefix={`${props.dataTestIdPrefix}-excel-export-fields`}
              hasFilter={queryString.length > 0}
              onToggleAll={handleToggleAll}
              onChange={handleFieldsChange}
              filteredItems={getFilteredFieldNames()}
              items={fieldNames}
              headerMap={{
                Included: appstr("Fields that can be chosen for Excel export"),
                Excluded: appstr("Fields that cannot be chosen for Excel export")
              }}
            />
          )}
        </Flex>
      </Flex>
    </SectionCard>
  );
};

const SystemFields = [
  ...kEmployeeCountFields,
  "SubChart Level",
  "Level",
  ...kPositionCountFields,
  "Holder Count",
  "Allocation"
];

const getAllFieldNames = (props: Props) => {
  return (props.chartConfig?.headers ?? []).filter(header => !SystemFields.includes(header));
};
